import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { createBulkReservations } from "../../api/courts";
import { ICourt } from "../../models/ICourt";
import { ICreateReservationRequest, IReservationCategory, ReservationStatus } from "../../models/ICourtReservation";
import { ITournamentItem } from "../../models/ITournament";
import { setGeneralAlertMessage } from "../../store/slices/generalSlice";
import { useAppDispatch, useAppSelector } from "../../store/store";
import { hasAdminAccess } from "../../utils/authorization";
import { Alert, Button, DatePicker, Checkbox, Dialog, EmptyState, Field, FormGrid, Select, Textarea } from "../../ui";
import "../ReservationDialog/styles.scss";
import "./styles.scss";

type BulkReservationDialogProps = {
  courts: ICourt[];
  categories: IReservationCategory[];
  tournaments?: ITournamentItem[];
  isOpen: boolean;
  onClose: (update?: boolean) => void;
  initialCategoryId?: string;
};

const BulkReservationDialog = (props: BulkReservationDialogProps) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const isAdmin = hasAdminAccess(currentUser);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [selectedCourtIds, setSelectedCourtIds] = useState<string[]>([]);
  const [reservationCategoryId, setReservationCategoryId] = useState<string>("");
  const [tournamentId, setTournamentId] = useState<string>("");
  const [needBall, setNeedBall] = useState(false);
  const [notes, setNotes] = useState("");

  const initialCategoryName = props.initialCategoryId
    ? props.categories.find((c) => c.id === props.initialCategoryId)?.name
    : undefined;

  useEffect(() => {
    if (props.isOpen) {
      setErrorMessage("");
      setStartTime(null);
      setEndTime(null);
      setSelectedCourtIds([]);
      setReservationCategoryId(props.initialCategoryId ?? "");
      setTournamentId("");
      setNeedBall(false);
      setNotes("");
    }
  }, [props.isOpen, props.courts]);

  const handleClose = (update?: boolean) => {
    props.onClose(update);
    setErrorMessage("");
  };

  // Filter tournaments to those whose date range includes the chosen start time.
  // The bulk dialog creates same-day reservations so filtering by startTime is sufficient.
  const availableTournaments = useMemo(() => {
    const all = props.tournaments || [];
    if (!startTime) return all;
    const day = moment(startTime).startOf("day");
    return all.filter((t) => {
      const s = moment(t.startDate).startOf("day");
      const e = moment(t.endDate).endOf("day");
      return day.isSameOrAfter(s) && day.isSameOrBefore(e);
    });
  }, [props.tournaments, startTime]);

  useEffect(() => {
    if (!tournamentId) return;
    if (!availableTournaments.some((t) => t.id === tournamentId)) {
      setTournamentId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableTournaments]);

  const handleCourtToggle = (courtId: string) => {
    // Prevent toggling inactive courts
    const court = props.courts.find((c) => c.id === courtId);
    if (court && !court.isActive) {
      return;
    }

    setSelectedCourtIds((prev) => {
      if (prev.includes(courtId)) {
        return prev.filter((id) => id !== courtId);
      } else {
        return [...prev, courtId];
      }
    });
  };

  const handleSubmit = async () => {
    // Validation
    if (!startTime) {
      setErrorMessage("Please select a start time");
      return;
    }

    if (!endTime) {
      setErrorMessage("Please select an end time");
      return;
    }

    if (startTime >= endTime) {
      setErrorMessage("End time must be after start time");
      return;
    }

    if (selectedCourtIds.length === 0) {
      setErrorMessage("Please select at least one court");
      return;
    }

    // Validate that all selected courts are active
    const inactiveCourts = selectedCourtIds
      .map((id) => props.courts.find((c) => c.id === id))
      .filter((court) => court && !court.isActive);

    if (inactiveCourts.length > 0) {
      setErrorMessage("Cannot create reservations for inactive courts");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const bulkData: ICreateReservationRequest[] = selectedCourtIds.map((courtId) => ({
        courtId,
        startTime: moment(startTime).toISOString(),
        endTime: moment(endTime).toISOString(),
        notes: notes || undefined,
        reservationCategoryId: reservationCategoryId || undefined,
        tournamentId: isAdmin && tournamentId ? tournamentId : undefined,
        needBall: needBall || undefined,
        status: isAdmin ? ReservationStatus.Confirmed : undefined,
      }));

      await createBulkReservations({ reservations: bulkData });
      dispatch(
        setGeneralAlertMessage({
          message: `${selectedCourtIds.length} reservation(s) created successfully`,
          type: "success",
        })
      );
      handleClose(true);
    } catch (error: any) {
      setErrorMessage(error?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const activeCourts = props.courts.filter((court) => court.isActive);

  return (
    <Dialog
      open={props.isOpen}
      onClose={() => handleClose()}
      placement="side"
      title={initialCategoryName ? `Create ${initialCategoryName} Reservations` : "Create Bulk Reservations"}
      busy={loading}
      footer={
        <>
          <Button variant="ghost" onClick={() => handleClose()} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            {`Create ${selectedCourtIds.length || 0} Reservation${selectedCourtIds.length !== 1 ? "s" : ""}`}
          </Button>
        </>
      }
    >
      <div className="rsv-form">
        {errorMessage && <Alert tone="danger">{errorMessage}</Alert>}

        {!props.initialCategoryId && (
          <section className="rsv-form__section">
            <h3 className="rsv-form__section-title">1. Category</h3>
            <Select
              block
              aria-label="Category"
              value={reservationCategoryId}
              onChange={(e) => setReservationCategoryId(e.target.value)}
              disabled={loading}
            >
              <option value="">No category</option>
              {props.categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name} (EGP {cat.pricePerHour}/hr)
                </option>
              ))}
            </Select>
          </section>
        )}

        {isAdmin && props.tournaments && props.tournaments.length > 0 && (
          <section className="rsv-form__section">
            <h3 className="rsv-form__section-title">
              Tournament <span className="rsv-form__optional">(optional)</span>
            </h3>
            <Select
              block
              aria-label="Tournament"
              value={tournamentId}
              onChange={(e) => setTournamentId(e.target.value)}
              disabled={loading || !startTime}
            >
              <option value="">
                {!startTime
                  ? "Select a start time first"
                  : availableTournaments.length === 0
                  ? "No tournaments on this date"
                  : "No tournament"}
              </option>
              {availableTournaments.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} · {moment(t.startDate).format("DD MMM")} – {moment(t.endDate).format("DD MMM YYYY")}
                </option>
              ))}
            </Select>
          </section>
        )}

        <section className="rsv-form__section">
          <h3 className="rsv-form__section-title">{props.initialCategoryId ? "1" : "2"}. Select Time</h3>
          <FormGrid columns={2}>
            <Field label="Start Time" required>
              {(id) => (
                <DatePicker
                  id={id}
                  selected={startTime}
                  onChange={(date) => setStartTime(date)}
                  mode="datetime"
                  timeIntervals={15}
                  dateFormat="MMM dd, yyyy hh:mm aa"
                  placeholderText="Select start time"
                  disabled={loading}
                  minDate={new Date()}
                />
              )}
            </Field>

            <Field label="End Time" required>
              {(id) => (
                <DatePicker
                  id={id}
                  selected={endTime}
                  onChange={(date) => setEndTime(date)}
                  mode="datetime"
                  timeIntervals={15}
                  dateFormat="MMM dd, yyyy hh:mm aa"
                  placeholderText="Select end time"
                  disabled={loading}
                  minDate={startTime || new Date()}
                />
              )}
            </Field>
          </FormGrid>
        </section>

        <section className="rsv-form__section">
          <h3 className="rsv-form__section-title">{props.initialCategoryId ? "2" : "3"}. Select Courts</h3>
          {activeCourts.length === 0 ? (
            <EmptyState compact title="No active courts available" />
          ) : (
            <div className="bulk-courts">
              <div className="bulk-courts__head">
                <span className="bulk-courts__count">
                  {selectedCourtIds.length} of {activeCourts.length} selected
                </span>
                {selectedCourtIds.length > 0 && (
                  <Button variant="link" size="sm" onClick={() => setSelectedCourtIds([])} disabled={loading}>
                    Clear
                  </Button>
                )}
              </div>
              <div className="bulk-courts__list">
                {[...props.courts]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((court) => {
                    const isDisabled = !court.isActive;
                    return (
                      <Checkbox
                        key={court.id}
                        className="bulk-courts__item"
                        checked={selectedCourtIds.includes(court.id)}
                        onChange={() => handleCourtToggle(court.id)}
                        disabled={loading || isDisabled}
                        label={`${court.name} ${!court.isActive ? "(Inactive)" : ""}`}
                      />
                    );
                  })}
              </div>
            </div>
          )}
        </section>

        <Checkbox checked={needBall} onChange={(checked) => setNeedBall(checked)} disabled={loading} label="Need Ball (+50 EGP)" />

        <section className="rsv-form__section">
          <h3 className="rsv-form__section-title">{props.initialCategoryId ? "3" : "4"}. Add Notes (Optional)</h3>
          <Textarea
            aria-label="Notes"
            placeholder="Additional notes for all reservations (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            disabled={loading}
          />
        </section>
      </div>
    </Dialog>
  );
};

export default BulkReservationDialog;
